import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cx } from "../../lib/cx";
import { Icon } from "./icon";

/**
 * A small static label: a tag, a count, a status. Not a control — a chip
 * that toggles is `MultiChips`. A2's `Chip` (`ui.tsx:90`).
 *
 * `surface` is A2's `white` tone (a surface pill with a hairline), kept
 * because the plan header and the deck card tags use it; A2's `dark` tone had
 * no caller and became `inverse`, the solid ink pill.
 */

export type ChipTone = "neutral" | "accent" | "success" | "inverse" | "surface";

const TONE: Record<ChipTone, string> = {
  neutral: "bg-surface-2 text-ink-2",
  accent: "bg-accent-soft text-accent-ink",
  success: "bg-success-soft text-success-ink",
  inverse: "bg-inverse text-on-inverse",
  surface: "bg-surface text-ink-2 ring-1 ring-line",
};

export interface ChipProps {
  children: ReactNode;
  className?: string;
  icon?: LucideIcon;
  tone?: ChipTone;
}

export function Chip({ tone, icon, className, children }: ChipProps) {
  return (
    <span
      className={cx(
        "inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 font-medium text-sm",
        TONE[tone ?? "neutral"],
        className
      )}
    >
      {icon ? <Icon icon={icon} size={16} /> : null}
      {children}
    </span>
  );
}
